import type { OntologyApiNode } from '@/types/ontologyGraph';
import type { Gene } from '@/types/ontologyGraph';
import { API_URL } from '@/components/Constants';
import { useAuthStore } from '@/store/authStore';

type RawDiseaseNode = {
  identity?: number;
  labels?: string[];
  properties?: Record<string, string>;
  elementId?: string;
};

type RawGeneNode = {
  identity?: number;
  labels?: string[];
  properties?: Record<string, string>;
  elementId?: string;
};

type RawSubclassItem = {
  genes?: RawGeneNode[];
  geneCount?: number;
  disease: RawDiseaseNode;
  subclasses?: RawSubclassItem[];
};

type RawGraph = {
  rootDisease: RawDiseaseNode;
  subclasses: RawSubclassItem[];
};

type RawRecord = {
  graph: RawGraph;
};

type RawPayload = {
  status?: string;
  records?: RawRecord[];
};

type MockResponse = {
  status?: string;
  payload?: RawPayload;
};

/** Formato antiguo (fallback): payload.graph.nodes */
type RawNode = {
  id: number;
  labels?: string[];
  properties?: Record<string, string[] | string>;
};

type RawGraphPayloadLegacy = {
  status?: string;
  graph?: {
    nodes?: RawNode[];
    relationships?: RawRelationship[];
  };
};

type RawRelationship = {
  id?: number | string;
  type?: string;
  startNode?: number | string;
  endNode?: number | string;
  start?: number | string;
  end?: number | string;
  source?: number | string;
  target?: number | string;
  properties?: Record<string, string[] | string>;
};

const LABEL_KEY_LEGACY = 'http__//www.w3.org/2000/01/rdf-schema#label';

export const GENE_NODE_ID_PREFIX = 'gene_';

let cachedNodes: OntologyApiNode[] | null = null;
let cachedGenesByDiseaseId: Map<string, Gene[]> | null = null;
let cachedQueryKey: string | null = null;

function getFirst(value: string[] | string | undefined): string | undefined {
  if (value == null) return undefined;
  if (Array.isArray(value)) return value.length > 0 ? value[0] : undefined;
  return typeof value === 'string' ? value : undefined;
}

function diseaseToNode(disease: RawDiseaseNode, parentId: string | undefined): OntologyApiNode | null {
  const props = disease.properties ?? {};
  const id = props.id != null ? String(props.id) : undefined;
  const name = props.name ?? (id ? `Node ${id}` : undefined);
  if (!id || !name) return null;
  const maybeGeneCount = props.geneCount != null ? Number(props.geneCount) : undefined;
  const geneCount = Number.isFinite(maybeGeneCount) ? maybeGeneCount : undefined;
  const cui = props.id?.match(/^C\d+$/) ? props.id : undefined;
  return {
    id,
    label: name,
    hierarchicalParent: parentId,
    hasHierarchicalChildren: false,
    numHierarchicalDescendants: 0,
    ...(geneCount != null ? { geneCount } : {}),
    ...(cui ? { cui } : {}),
  };
}

function rawGeneToGene(raw: RawGeneNode): Gene | null {
  const props = raw.properties ?? {};
  const id = props.id != null ? String(props.id) : undefined;
  const symbol = props.symbol ?? props.fullname ?? id;
  if (!id) return null;
  return {
    id,
    symbol: symbol ?? id,
    fullName: props.fullname ?? props.description,
  };
}

function parseNewFormat(payload: RawPayload): {
  nodes: OntologyApiNode[];
  genesByDiseaseId: Map<string, Gene[]>;
} {
  const records = payload?.records ?? [];
  const genesByDiseaseId = new Map<string, Gene[]>();

  if (records.length === 0) return { nodes: [], genesByDiseaseId };

  const nodeById = new Map<string, OntologyApiNode>();
  const ensureNode = (disease: RawDiseaseNode, parentId: string | undefined): string | null => {
    const node = diseaseToNode(disease, parentId);
    if (!node) return null;
    const existing = nodeById.get(node.id);
    if (!existing) {
      nodeById.set(node.id, node);
      return node.id;
    }

    if (parentId && !existing.hierarchicalParent) {
      existing.hierarchicalParent = parentId;
    }
    if (node.geneCount != null) {
      existing.geneCount = node.geneCount;
    }
    existing.label = existing.label || node.label;
    return existing.id;
  };

  const walkSubclass = (item: RawSubclassItem, parentId: string) => {
    const diseaseId = ensureNode(item.disease, parentId);
    if (!diseaseId) return;

    const props = item.disease.properties ?? {};
    const subclassGeneCount = Number(item.geneCount);
    const propsGeneCount = Number(props.geneCount);
    const resolvedGeneCount = Number.isFinite(subclassGeneCount)
      ? subclassGeneCount
      : Number.isFinite(propsGeneCount)
        ? propsGeneCount
        : undefined;
    if (resolvedGeneCount != null) {
      const node = nodeById.get(diseaseId);
      if (node) node.geneCount = resolvedGeneCount;
    }

    const genes = (item.genes ?? [])
      .map((g: RawGeneNode) => rawGeneToGene(g))
      .filter((g): g is Gene => g != null);
    if (genes.length > 0) {
      const existing = genesByDiseaseId.get(diseaseId) ?? [];
      const seen = new Set(existing.map(g => g.id));
      for (const g of genes) {
        if (!seen.has(g.id)) {
          existing.push(g);
          seen.add(g.id);
        }
      }
      genesByDiseaseId.set(diseaseId, existing);
    }

    for (const child of item.subclasses ?? []) {
      walkSubclass(child, diseaseId);
    }
  };

  for (const record of records) {
    const graph = record?.graph;
    if (!graph?.rootDisease) continue;

    const rootId = ensureNode(graph.rootDisease, undefined);
    if (!rootId) continue;

    for (const item of graph.subclasses ?? []) {
      walkSubclass(item, rootId);
    }
  }

  const childCountByParent = new Map<string, number>();
  for (const node of nodeById.values()) {
    if (!node.hierarchicalParent) continue;
    childCountByParent.set(
      node.hierarchicalParent,
      (childCountByParent.get(node.hierarchicalParent) ?? 0) + 1,
    );
  }
  for (const node of nodeById.values()) {
    const directChildren = childCountByParent.get(node.id) ?? 0;
    node.numHierarchicalDescendants = directChildren;
    node.hasHierarchicalChildren = directChildren > 0;
  }

  return { nodes: Array.from(nodeById.values()), genesByDiseaseId };
}

function pickProperty(props: Record<string, string[] | string>, key: string): string | undefined {
  return getFirst(props[key]);
}

function resolveCanonicalNodeId(props: Record<string, string[] | string>, numericId: number): string {
  const preferred = pickProperty(props, 'id') ?? pickProperty(props, 'umls');
  if (preferred) {
    const first = preferred.split('|')[0]?.trim();
    if (first) return first;
  }
  return String(numericId);
}

function resolveCui(props: Record<string, string[] | string>, canonicalId: string): string | undefined {
  if (/^C\d+$/.test(canonicalId)) return canonicalId;
  const umls = pickProperty(props, 'umls');
  if (!umls) return undefined;
  const match = umls.match(/C\d+/);
  return match?.[0];
}

function relationshipEndpoints(rel: RawRelationship): { start?: string; end?: string } {
  const start = rel.startNode ?? rel.start ?? rel.source;
  const end = rel.endNode ?? rel.end ?? rel.target;
  return {
    start: start != null ? String(start) : undefined,
    end: end != null ? String(end) : undefined,
  };
}

function isHierarchyRelationship(type: string | undefined): boolean {
  if (!type) return true;
  const normalized = type.toUpperCase();
  return (
    normalized.includes('IS_A') ||
    normalized.includes('SUBCLASS') ||
    normalized.includes('PARENT') ||
    normalized.includes('CHILD') ||
    normalized.includes('HIERARCH')
  );
}

function normalizeGraphNodes(
  rawNodes: RawNode[],
  rawRelationships: RawRelationship[] = [],
): OntologyApiNode[] {
  const numericToCanonical = new Map<string, string>();
  const nodesById = new Map<string, OntologyApiNode>();

  for (const n of rawNodes) {
    const props = n.properties ?? {};
    const canonicalId = resolveCanonicalNodeId(props, n.id);
    numericToCanonical.set(String(n.id), canonicalId);

    const label =
      pickProperty(props, 'name') ??
      getFirst(props[LABEL_KEY_LEGACY] as string[] | string | undefined) ??
      `Node ${canonicalId}`;
    const maybeGeneCount = pickProperty(props, 'geneCount');
    const geneCount = maybeGeneCount != null ? Number(maybeGeneCount) : undefined;
    const cui = resolveCui(props, canonicalId);

    const existing = nodesById.get(canonicalId);
    if (existing) {
      existing.label = existing.label || label;
      if (geneCount != null && Number.isFinite(geneCount)) {
        existing.geneCount = geneCount;
      }
      if (cui) existing.cui = cui;
      continue;
    }

    nodesById.set(canonicalId, {
      id: canonicalId,
      label,
      hasHierarchicalChildren: false,
      numHierarchicalDescendants: 0,
      ...(Number.isFinite(geneCount) ? { geneCount } : {}),
      ...(cui ? { cui } : {}),
    });
  }

  const resolveEndpoint = (endpoint: string | undefined): string | undefined => {
    if (!endpoint) return undefined;
    return numericToCanonical.get(endpoint) ?? endpoint;
  };

  for (const rel of rawRelationships) {
    const { start, end } = relationshipEndpoints(rel);
    if (!start || !end || !isHierarchyRelationship(rel.type)) continue;

    const childId = resolveEndpoint(start);
    const parentId = resolveEndpoint(end);
    if (!childId || !parentId || childId === parentId) continue;

    const child = nodesById.get(childId);
    if (!child || child.hierarchicalParent) continue;
    child.hierarchicalParent = parentId;
  }

  const childCountByParent = new Map<string, number>();
  for (const node of nodesById.values()) {
    if (!node.hierarchicalParent) continue;
    childCountByParent.set(
      node.hierarchicalParent,
      (childCountByParent.get(node.hierarchicalParent) ?? 0) + 1,
    );
  }
  for (const node of nodesById.values()) {
    const directChildren = childCountByParent.get(node.id) ?? 0;
    node.numHierarchicalDescendants = directChildren;
    node.hasHierarchicalChildren = directChildren > 0;
  }

  return Array.from(nodesById.values());
}

/** @deprecated Use normalizeGraphNodes */
function normalizeNodesLegacy(rawNodes: RawNode[]): OntologyApiNode[] {
  return normalizeGraphNodes(rawNodes);
}

async function loadMockGraph(): Promise<OntologyApiNode[]> {
  if (cachedNodes) return cachedNodes;

  const res = await fetch('/Response-graph.json');
  if (!res.ok) throw new Error('Failed to load ontology graph');
  const data: MockResponse = await res.json();
  const payload = data.payload;

  if (payload && 'records' in payload && Array.isArray((payload as RawPayload).records)) {
    const out = parseNewFormat(payload as RawPayload);
    cachedNodes = out.nodes;
    cachedGenesByDiseaseId = out.genesByDiseaseId;
  } else {
    const { nodes: rawNodes, relationships: rawRelationships } = extractGraphPayload(data);
    cachedNodes = normalizeGraphNodes(rawNodes, rawRelationships);
    cachedGenesByDiseaseId = new Map();
  }

  return cachedNodes;
}

type OntologyQuery = {
  disease: string;
  ontoID?: 'id' | 'name';
};

const ONTOLOGY_FALLBACK_ENABLED = process.env.REACT_APP_ONTOLOGY_GRAPH_LOCAL_FALLBACK === 'true';

function getAuthorizationToken(): string {
  const storeToken = useAuthStore.getState().authorization;
  if (storeToken) return storeToken;

  // Fallback for page reload race: auth store may hydrate after first request.
  try {
    const keys = Object.keys(window.localStorage);
    const idTokenKey = keys.find(key => key.endsWith('.idToken'));
    if (idTokenKey) return window.localStorage.getItem(idTokenKey) ?? '';
  } catch {
    return '';
  }
  return '';
}

function queryKeyOf(query: OntologyQuery): string {
  return `${query.ontoID ?? 'id'}::${query.disease}`;
}

function extractGraphPayload(data: MockResponse): {
  nodes: RawNode[];
  relationships: RawRelationship[];
} {
  const payload = data.payload as RawGraphPayloadLegacy | undefined;
  const graph =
    payload?.graph ?? (data as unknown as { graph?: RawGraphPayloadLegacy['graph'] }).graph ?? undefined;
  return {
    nodes: graph?.nodes ?? [],
    relationships: (graph?.relationships ?? []) as RawRelationship[],
  };
}

function parseOntologyGraphResponse(data: MockResponse, queryKey: string): OntologyApiNode[] {
  const topLevelStatus =
    typeof (data as unknown as { status?: unknown }).status === 'string'
      ? ((data as unknown as { status: string }).status ?? '')
      : '';
  if (topLevelStatus.toUpperCase().startsWith('ERROR')) {
    throw new Error(topLevelStatus);
  }

  const payload = data.payload;
  if (payload && 'records' in payload && Array.isArray((payload as RawPayload).records)) {
    const out = parseNewFormat(payload as RawPayload);
    cachedNodes = out.nodes;
    cachedGenesByDiseaseId = out.genesByDiseaseId;
    cachedQueryKey = queryKey;
    return cachedNodes;
  }

  const { nodes: rawNodes, relationships: rawRelationships } = extractGraphPayload(data);
  const legacyStatus = (data.payload as RawGraphPayloadLegacy | undefined)?.status;
  if (typeof legacyStatus === 'string' && legacyStatus.toUpperCase().startsWith('ERROR')) {
    throw new Error(legacyStatus);
  }
  cachedNodes = normalizeGraphNodes(rawNodes, rawRelationships);
  cachedGenesByDiseaseId = new Map();
  cachedQueryKey = queryKey;
  return cachedNodes;
}

export function isFlatOntologySearch(nodes: OntologyApiNode[]): boolean {
  if (nodes.length <= 1) return false;
  return !nodes.some(node => Boolean(node.hierarchicalParent));
}

function isUsableHierarchy(nodes: OntologyApiNode[], requestedDisease: string): boolean {
  if (nodes.length === 0) return false;
  const hasHierarchyLinks = nodes.some(node => Boolean(node.hierarchicalParent));
  const hasRequestedRoot = nodes.some(node => node.id === requestedDisease || node.cui === requestedDisease);
  const hasNamedResults = nodes.some(node => !node.label.startsWith('Node '));
  return hasHierarchyLinks || hasRequestedRoot || hasNamedResults;
}

async function loadLocalFallbackGraph(
  queryKey: string,
  requestedDisease: string,
): Promise<OntologyApiNode[]> {
  const fallbackPaths = ['/Response.json', '/Response-graph.json'];
  for (const path of fallbackPaths) {
    const res = await fetch(path);
    if (!res.ok) continue;
    const data: MockResponse = await res.json();
    const parsed = parseOntologyGraphResponse(data, queryKey);
    if (isUsableHierarchy(parsed, requestedDisease)) {
      return parsed;
    }
  }
  throw new Error('Fallback ontology file has no usable hierarchy');
}

async function loadGraphForQuery(query: OntologyQuery): Promise<OntologyApiNode[]> {
  const normalized = query.disease.trim();
  const queryKey = queryKeyOf({ ...query, disease: normalized });
  if (cachedNodes && cachedQueryKey === queryKey) return cachedNodes;

  const params = new URLSearchParams({
    disease: normalized,
    ontoID: query.ontoID ?? 'id',
  });
  const authorization = getAuthorizationToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    accept: 'application/json',
    ...(authorization ? { Authorization: authorization } : {}),
  };

  try {
    const res = await fetch(`${API_URL}api/ontology/graph?${params.toString()}`, { headers });
    if (!res.ok) throw new Error('Failed to load ontology graph');
    const data: MockResponse = await res.json();
    return parseOntologyGraphResponse(data, queryKey);
  } catch (error) {
    if (ONTOLOGY_FALLBACK_ENABLED) {
      return loadLocalFallbackGraph(queryKey, normalized);
    }
    throw error;
  }
}

export async function getGenesForDisease(diseaseId: string): Promise<Gene[]> {
  await loadMockGraph();
  return cachedGenesByDiseaseId?.get(diseaseId) ?? [];
}

export async function getDiseasesForGene(geneId: string): Promise<{ id: string; label: string }[]> {
  await loadMockGraph();
  const result: { id: string; label: string }[] = [];
  if (!cachedGenesByDiseaseId || !cachedNodes) return result;
  for (const [diseaseId, genes] of cachedGenesByDiseaseId) {
    if (genes.some(g => g.id === geneId)) {
      const node = cachedNodes.find(n => n.id === diseaseId);
      result.push({ id: diseaseId, label: node?.label ?? diseaseId });
    }
  }
  return result;
}

export async function getOntologyNode(id: string): Promise<OntologyApiNode | null> {
  const nodes = await loadMockGraph();
  const byId = nodes.find(n => n.id === id);
  if (byId) return byId;
  return nodes.find(n => n.cui === id) ?? null;
}

export async function getOntologyChildren(id: string): Promise<OntologyApiNode[]> {
  const nodes = await loadMockGraph();
  return nodes.filter(n => n.hierarchicalParent === id);
}

export async function getExplorerRoots(limit = 10): Promise<OntologyApiNode[]> {
  const nodes = await loadMockGraph();
  const trueRoots = nodes.filter(n => !n.hierarchicalParent);
  if (trueRoots.length >= limit) return trueRoots.slice(0, limit);

  const firstRootId = trueRoots[0]?.id;
  const children = firstRootId ? nodes.filter(n => n.hierarchicalParent === firstRootId) : [];
  return [...trueRoots, ...children].slice(0, limit);
}

export type OntologyFullGraph = {
  nodes: OntologyApiNode[];
  edges: { source: string; target: string }[];
};

export async function getOntologyFullGraph(): Promise<OntologyFullGraph> {
  const nodes = await loadMockGraph();
  const edges = nodes
    .filter(n => n.hierarchicalParent != null)
    .map(n => ({ source: n.hierarchicalParent!, target: n.id }));
  return { nodes, edges };
}

export async function searchOntologyGraph(query: OntologyQuery): Promise<OntologyApiNode[]> {
  return loadGraphForQuery(query);
}
